import React, { ForwardedRef, forwardRef, useRef } from 'react';
import * as cn from 'classnames';

import { Handle, Item, Plus, Remove } from '../Item';

import styles from './Container.module.scss';
import EmojiPicker from '../EmojiPicker/EmojiPicker';
import { uuid } from '../../types/types';
// import { ActionDropdown } from '../ActionDropdown/ActionDropdown';
import { getListIds, useTaskListStore } from '../../stores/useTaskListStore';
import {
  Avatar,
  Box,
  Button,
  IconButton,
  Card,
  DropdownMenu,
  Flex,
  Text,
  Callout,
  AlertDialog,
} from '@radix-ui/themes';
import {
  DotsHorizontalIcon,
  Pencil2Icon,
  HandIcon,
  DownloadIcon,
} from '@radix-ui/react-icons';
export interface Props {
  id: uuid;
  children: React.ReactNode;
  columns?: number;
  label?: string | React.ReactNode;
  icon?: string;
  iconMutable?: boolean;
  style?: React.CSSProperties;
  horizontal?: boolean;
  hover?: boolean;
  handleProps?: React.HTMLAttributes<any>;
  scrollable?: boolean;
  shadow?: boolean;
  placeholder?: boolean;
  unstyled?: boolean;
  showActions?: boolean;
  showDroppable?: boolean;
  /// if true, the container will be rendered as a button
  useButton?: boolean;
  /// footer
  footer?: React.ReactNode;

  onClick?(event?: React.MouseEvent<HTMLElement>): void;

  onAddNewItemClick?(options?: any): void;

  // onRemove?(): void;
}

const AddNewItem = ({
  onAddNewItemClick,
}: {
  onAddNewItemClick?: (options: any) => void;
}) => {
  return (
    <IconButton
      variant="ghost"
      className={styles.addNewItemButton}
      onClick={onAddNewItemClick}
    >
      <Pencil2Icon width="16" height="16" />
    </IconButton>
  );
};

/**
 * TODO: move this into a separate component
 */
const ActionDropdown: React.FC<{ id: uuid }> = ({ id }) => {
  const removeUserList = useTaskListStore((state) => state.removeUserList);
  const itemsMap = useTaskListStore((state) => state.itemsMap);
  const listItemsCount = itemsMap[id]?.length || 0;
  const needsConfirm = listItemsCount > 0;

  const isInitialList =
    id === '223270cb-72e5-43be-959c-d4e1744a96a6' || // IMPORTANT_URGENT
    id === '71c4cdf4-0ff3-4722-8069-47a2bb9dd3de' || // URGENT
    id === 'c3565f9e-50e4-4a96-966b-ba95ea2c3292'; // LOW_PRIORITY (Inbox)

  return (
    <AlertDialog.Root>
      <DropdownMenu.Root>
        <DropdownMenu.Trigger asChild>
          <IconButton variant="ghost" className={styles.triggerButton}>
            <DotsHorizontalIcon width="18" height="18" />
          </IconButton>
        </DropdownMenu.Trigger>
        <DropdownMenu.Content className={styles.content} align="end">
          {needsConfirm ? (
            <AlertDialog.Trigger>
              <DropdownMenu.Item color="red" disabled={isInitialList}>
                Delete List
              </DropdownMenu.Item>
            </AlertDialog.Trigger>
          ) : (
            <DropdownMenu.Item
              color="red"
              disabled={isInitialList}
              onSelect={() => removeUserList(id)}
            >
              Delete List
            </DropdownMenu.Item>
          )}
        </DropdownMenu.Content>
      </DropdownMenu.Root>

      {needsConfirm && (
        <AlertDialog.Content maxWidth="400px">
          <AlertDialog.Title>Delete List</AlertDialog.Title>
          <AlertDialog.Description size="2">
            This list and all tasks in it will be permanently deleted.
          </AlertDialog.Description>

          <Flex gap="3" mt="4" justify="end">
            <AlertDialog.Cancel>
              <Button variant="soft" color="gray">
                Cancel, keep it
              </Button>
            </AlertDialog.Cancel>
            <AlertDialog.Action>
              <Button
                variant="solid"
                color="red"
                onClick={() => {
                  removeUserList(id);
                }}
              >
                Delete List
              </Button>
            </AlertDialog.Action>
          </Flex>
        </AlertDialog.Content>
      )}
    </AlertDialog.Root>
  );
};

const DroppableIndicator: React.FC<{ showDroppable: boolean }> = ({
  showDroppable,
}) => {
  return showDroppable ? (
    <div className={styles.DroppableIndicator}>
      <Box p="4">
        <Callout.Root
          size="3"
          color="gray"
          variant="soft"
          highContrast
          className={styles.DroppableIndicatorCallout}
        >
          <Callout.Icon>
            <DownloadIcon />
          </Callout.Icon>
          <Callout.Text>Drop to move it here</Callout.Text>
        </Callout.Root>
      </Box>
    </div>
  ) : null;
};

export const Container = forwardRef<HTMLDivElement, Props>(
  (
    {
      id,
      children,
      columns = 1,
      handleProps,
      horizontal,
      hover,
      onClick,
      // onRemove,
      onAddNewItemClick,
      label,
      icon,
      iconMutable,
      placeholder,
      style,
      scrollable,
      shadow,
      unstyled,
      showActions,
      showDroppable = false,
      useButton,
      footer,
      ...props
    }: Props,
    ref
  ) => {
    const tasksContainerRef = useRef<HTMLDivElement>(null);

    const listLabel = useTaskListStore(
      (state) =>
        state.userListMap[id]?.label ||
        state.taskListMap[id]?.label ||
        (typeof label === 'string' ? label : '')
    );
    const updateLabel = useTaskListStore((state) => state.updateLabel);

    return (
      <Flex
        direction="column"
        {...props}
        // @ts-ignore
        ref={ref}
        style={
          {
            ...style,
            '--columns': columns,
          } as React.CSSProperties
        }
        className={cn(
          styles.Container,
          unstyled && styles.unstyled,
          horizontal && styles.horizontal,
          hover && styles.hover,
          placeholder && styles.placeholder,
          // scrollable && styles.scrollable,
          shadow && styles.shadow,
          showDroppable && styles.showDroppable
        )}
        onClick={onClick}
      >
        {label ? (
          <Flex className={styles.Header} justify="between" align="center">
            <Flex gap="2" align="center" style={{ flex: 1, minWidth: 0 }}>
              {icon ? (
                iconMutable ? (
                  <EmojiPicker id={id} emoji={icon} />
                ) : (
                  <span className={styles.Icon}>{icon}</span>
                )
              ) : null}
              {typeof label === 'string' ? (
                <input
                  type="text"
                  value={listLabel}
                  onChange={(e) => updateLabel(id, e.target.value)}
                  className={styles.LabelInput}
                  placeholder="List Title"
                />
              ) : label ? (
                <span className={styles.Label}>{label}</span>
              ) : null}
            </Flex>
            {showActions ? (
              <Flex className={styles.Actions} gap="1" align="center">
                {/* Remove is for deleting, not needed now */}
                {/* {onRemove ? <Remove onClick={onRemove} /> : null} */}

                {/* Handle is for dragging & moving, not needed now */}
                {/* <Handle {...handleProps} /> */}
                <AddNewItem onAddNewItemClick={onAddNewItemClick} />
                <ActionDropdown id={id} />
              </Flex>
            ) : null}
          </Flex>
        ) : null}
        {placeholder ? (
          children
        ) : (
          <Flex
            className={cn(styles.Content, footer && styles.withFooter)}
            direction="column"
          >
            <Box p="2" pb="4">
              <Flex ref={tasksContainerRef} direction="column">
                {children}
              </Flex>
            </Box>

            {footer ? <Box className={styles.Footer}>{footer}</Box> : null}

            <DroppableIndicator showDroppable={showDroppable} />
          </Flex>
        )}
      </Flex>
    );
  }
);
